import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CommentBox from '../../../../../components/Shared/CommentBox/index.tsx';
// import Notepad from '../../../../../components/LeadWebForm/notepad.jsx';
import ModalForms from '../../../../../components/Shared/Modals';
import { ImageModal } from '../../../../../components/Modals/ImageModal/index';
import { AudioModal } from '../../../../../components/Modals/AudioModal/index';
import { LeadFormActions } from '../../../../../Services/_actions';
import { leadDetailObjAtom } from '../../../../../Services/_states/LeadForm';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useSnackBar } from '../../../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import {
  BLOCKED_EXTENSIONS,
  WHATSAPP_ALLOWED_MIME,
  WHATSAPP_LIMITS,
} from '../../../../../components/Whatsapp/Template/template.constant.js';
import { radiusPx } from '../../../styles/layoutCommon.js';

function inferDocType(mime) {
  if (WHATSAPP_ALLOWED_MIME.image.includes(mime)) return 'image';
  if (WHATSAPP_ALLOWED_MIME.audio.includes(mime)) return 'audio';
  return null;
}

const bytesToMB = (bytes) => (bytes / (1024 * 1024)).toFixed(2);

export default function NotesTab({ isDrawerOpen }) {
  const leadFormActions = LeadFormActions();
  const { showSnackBar } = useSnackBar();
const leadDetails = useRecoilValue(leadDetailObjAtom);
  const [commentList, setCommentList] = useState([]);
  const [showLastComment, setShowLastComment] = useState(true);
  const [imagesList, setImagesList] = useState([]);
  const [audioList, setAudioList] = useState([]);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [audioModalOpen, setAudioModalOpen] = useState(false);

 useEffect(() => {
  if (!isDrawerOpen || !leadDetails?.id) {
    setCommentList([]);
    return;
  }

  let isMounted = true;

  leadFormActions.getCommentById(leadDetails.id).then((res) => {
    if (isMounted) {
      setCommentList(res?.data?.internal_comments || []);
    }
  });

  return () => {
    isMounted = false;
  };
}, [isDrawerOpen, leadDetails?.id]);

  const onSubmitComment = async (commentText) => {
    if (!commentText || !leadDetails?.id) return;
    try {
      const response = await leadFormActions.postCommentById(
        { comment: commentText, id: leadDetails.id },
        leadDetails.id
      );
      if (response?.status === 200) {
        setCommentList((prev) => [
          ...prev,
          { comment: commentText, created_at: new Date(), created_by: 'You' },
        ]);
        showSnackBar('Comment added successfully', 'success');
      }
    } catch (err) {
      showSnackBar(err?.toString() || 'Failed to add comment', 'error');
    }
  };

  const handleFileSelect = async (event, type) => {
    const file = event?.target?.files?.[0];
    if (!file || !leadDetails?.id) return;

    const ext = file.name.toLowerCase().split('.').pop();
    if (BLOCKED_EXTENSIONS.has(ext)) {
      showSnackBar(`"${ext}" files are not allowed`, 'error');
      event.target.value = '';
      return;
    }

    const typeGroup = inferDocType(file.type);
    if (type === 'audio' && typeGroup !== 'audio') {
      showSnackBar('Please upload an audio file', 'error');
      event.target.value = '';
      return;
    }
    if (type === 'image' && typeGroup !== 'image') {
      showSnackBar('Please upload an image file', 'error');
      event.target.value = '';
      return;
    }

    const limit = WHATSAPP_LIMITS[typeGroup];
    if (file.size > limit) {
      showSnackBar(`File too large. Max ${bytesToMB(limit)} MB`, 'error');
      event.target.value = '';
      return;
    }

    const form = new FormData();
    form.append('file', file);
    if (type === 'image') {
      await leadFormActions.uploadLeadImageById(leadDetails.id, form);
    } else {
      await leadFormActions.uploadLeadAudioById(leadDetails.id, form);
    }
    showSnackBar('File uploaded successfully', 'success');
    event.target.value = '';
  };

  const openImages = async () => {
    if (!leadDetails?.id) return;
    const res = await leadFormActions.getLeadImagesById(leadDetails.id);
    setImagesList(res?.data?.image_funnel || []);
    setImageModalOpen(true);
  };

  const openAudio = async () => {
    if (!leadDetails?.id) return;
    const res = await leadFormActions.getLeadAudioById(leadDetails.id);
    setAudioList(res?.data?.audio_list || []);
    setAudioModalOpen(true);
  };

  if (!leadDetails?.id) {
    return (
      <Typography variant="body2" color="text.secondary">
        Select or create a lead to add notes and files.
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Internal Comments
      </Typography>
      <Typography
        variant="caption"
        color="primary"
        sx={{ cursor: 'pointer', display: 'block', mb: 1 }}
        onClick={() => setShowLastComment((prev) => !prev)}
      >
        {showLastComment
          ? `View All Comments${commentList.length ? ` (${commentList.length})` : ''}`
          : 'Hide All Comments'}
      </Typography>
      <CommentBox
        commentList={showLastComment && leadDetails?.comment?.comment
          ? [leadDetails.comment]
          : commentList}
        onSubmitComment={onSubmitComment}
      />

      {/* <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Private Notepad
        </Typography>
        <Notepad />
      </Box> */}

      <Accordion disableGutters elevation={0} sx={{ mt: 2, border: '1px solid', borderColor: 'divider' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Files</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Images
            </Typography>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                p: 2,
                textAlign: 'center',
                cursor: 'pointer',
                borderRadius: 1,
              }}
              onClick={() => document.getElementById('intervene-image-upload')?.click()}
            >
              <CloudUploadOutlinedIcon color="primary" />
              <input
                type="file"
                id="intervene-image-upload"
                accept="image/png, image/gif, image/jpeg"
                onChange={(e) => handleFileSelect(e, 'image')}
                style={{ display: 'none' }}
              />
            </Box>
            <Button size="small" variant="contained" sx={{ mt: 1 , borderRadius: radiusPx.md }} onClick={openImages}>
              View Images
            </Button>
          </Box>

          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Recordings
            </Typography>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                p: 2,
                textAlign: 'center',
                cursor: 'pointer',
                borderRadius: 1,
              }}
              onClick={() => document.getElementById('intervene-audio-upload')?.click()}
            >
              <CloudUploadOutlinedIcon color="primary" />
              <input
                type="file"
                id="intervene-audio-upload"
                accept="audio/*"
                onChange={(e) => handleFileSelect(e, 'audio')}
                style={{ display: 'none' }}
              />
            </Box>
            <Button size="small" variant="contained" sx={{ mt: 1 , borderRadius: radiusPx.md }} onClick={openAudio}>
              View Recordings
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

      {imageModalOpen && (
        <ModalForms
          show={imageModalOpen}
          handleClose={() => setImageModalOpen(false)}
          modalBody={<ImageModal imagesList={imagesList} />}
          modalHeader="Images"
          modalId="interveneImages"
        />
      )}

      {audioModalOpen && (
        <ModalForms
          show={audioModalOpen}
          handleClose={() => setAudioModalOpen(false)}
          modalBody={<AudioModal audioList={audioList} />}
          modalHeader="Audio"
          modalId="interveneAudio"
        />
      )}
    </Box>
  );
}
